import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ToolCard from "../components/ToolCard";
import ToolFAQ from "../components/ToolFAQ";
import JsonLD, { buildBreadcrumb, buildFAQSchema } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { EXCEL_TOOLS_LIST, SITE_NAME, SITE_URL } from "../translations";
import { useHreflang } from "../hooks/useHreflang";
import { buildLocalizedPath } from "../i18nConfig";

const meta = {
  en: {
    title: "Free Excel Tools Online — Convert Excel to CSV & PDF",
    h1: "Free Excel Tools Online",
    description:
      "Free online Excel tools by FileNesto: convert Excel to CSV, export Excel to PDF, or import CSV into Excel (.xlsx) — no signup, files never stored.",
    intro:
      "Fast, free tools to convert and transform your spreadsheets. Pick a tool below — all processing happens in your browser session and files are never stored.",
    seoText: "FileNesto's Excel suite provides three essential spreadsheet conversion tools, all free and available without registration. Excel to PDF converts any .xlsx spreadsheet into a clean, portable PDF document — ideal for sharing financial reports, project plans, or data tables with colleagues who don't use Excel. Excel to CSV exports the active sheet as a UTF-8 encoded CSV file, compatible with databases (MySQL, PostgreSQL), BI tools (Tableau, Power BI), Python pandas, R, and virtually any data system. CSV to Excel converts plain CSV files back into a structured Excel workbook (.xlsx), automatically handling UTF-8, UTF-8 BOM, and latin-1 encodings. All tools support files up to 20MB and process your data entirely in memory — nothing is ever stored on our servers.",
    faqs: [
      { q: "What Excel tools does FileNesto offer?", a: "Three free tools: Excel to PDF (convert spreadsheets to PDF), Excel to CSV (export to CSV), and CSV to Excel (import CSV into .xlsx). All free, no signup required." },
      { q: "What Excel format is supported?", a: ".xlsx files (Excel 2007 and later). If you have an older .xls file, open it in Excel and save as .xlsx first." },
      { q: "Does Excel to PDF include charts?", a: "The converter exports cell data in a table format. Charts, images, and complex formatting are not included — the output is a clean data PDF." },
      { q: "Is the CSV output compatible with Google Sheets?", a: "Yes. The output is a standard UTF-8 CSV compatible with Google Sheets, Excel, MySQL, pandas, and most data tools." },
    ],
  },
  fr: {
    title: "Outils Excel gratuits en ligne — Convertir Excel en PDF et CSV",
    h1: "Outils Excel gratuits en ligne",
    description:
      "Outils Excel en ligne gratuits par FileNesto : convertir Excel en PDF, exporter en CSV ou importer un CSV en tableau Excel (.xlsx) — sans inscription, fichiers jamais stockés.",
    intro:
      "Des outils rapides et gratuits pour convertir et transformer vos feuilles de calcul. Choisissez un outil ci-dessous — le traitement se fait dans votre session et les fichiers ne sont jamais stockés.",
    seoText: "La suite Excel de FileNesto propose trois outils essentiels de conversion de tableurs, tous gratuits et disponibles sans inscription. Excel vers PDF, Excel vers CSV et CSV vers Excel — sans installation ni compte requis.",
    faqs: [
      { q: "Quels outils Excel propose FileNesto ?", a: "Trois outils gratuits : Excel vers PDF, Excel vers CSV et CSV vers Excel. Tous gratuits, sans inscription." },
      { q: "Quel format Excel est supporté ?", a: "Les fichiers .xlsx (Excel 2007 et ultérieur). Pour un fichier .xls, ouvrez-le dans Excel et enregistrez-le en .xlsx." },
      { q: "Excel vers PDF inclut-il les graphiques ?", a: "Le convertisseur exporte les données des cellules en tableau. Les graphiques, images et mises en forme complexes ne sont pas inclus." },
      { q: "Le CSV est-il compatible avec Google Sheets ?", a: "Oui. Le résultat est un CSV UTF-8 standard compatible avec Google Sheets, Excel, MySQL, pandas et la plupart des outils de données." },
    ],
  },
  es: {
    title: "Herramientas Excel gratuitas en línea — Convertir Excel a PDF y CSV",
    h1: "Herramientas Excel gratuitas en línea",
    description: "Herramientas Excel en línea gratuitas de FileNesto: convierte Excel a PDF, exporta a CSV o importa un CSV en Excel (.xlsx) — sin registro, archivos nunca almacenados.",
    intro: "Herramientas rápidas y gratuitas para convertir y transformar tus hojas de cálculo.",
    seoText: "La suite Excel de FileNesto ofrece tres herramientas esenciales de conversión de hojas de cálculo, todas gratuitas y disponibles sin registro.",
    faqs: [
      { q: "¿Qué herramientas Excel ofrece FileNesto?", a: "Tres herramientas gratuitas: Excel a PDF, Excel a CSV y CSV a Excel. Todas gratuitas, sin registro." },
      { q: "¿Qué formato Excel es compatible?", a: "Archivos .xlsx (Excel 2007 y posteriores)." },
      { q: "¿Excel a PDF incluye gráficos?", a: "El conversor exporta los datos de las celdas en formato tabla. Los gráficos e imágenes no están incluidos." },
      { q: "¿El CSV es compatible con Google Sheets?", a: "Sí. El resultado es un CSV UTF-8 estándar compatible con Google Sheets, Excel y pandas." },
    ],
  },
  pt: {
    title: "Ferramentas Excel gratuitas online — Converter Excel para PDF e CSV",
    h1: "Ferramentas Excel gratuitas online",
    description: "Ferramentas Excel online gratuitas do FileNesto: converta Excel para PDF, exporte para CSV ou importe um CSV para Excel (.xlsx) — sem cadastro, arquivos nunca armazenados.",
    intro: "Ferramentas rápidas e gratuitas para converter e transformar suas planilhas.",
    seoText: "O conjunto de ferramentas Excel do FileNesto oferece três ferramentas essenciais de conversão de planilhas, todas gratuitas e disponíveis sem cadastro.",
    faqs: [
      { q: "Quais ferramentas Excel o FileNesto oferece?", a: "Três ferramentas gratuitas: Excel para PDF, Excel para CSV e CSV para Excel. Todas gratuitas, sem cadastro." },
      { q: "Qual formato Excel é suportado?", a: "Arquivos .xlsx (Excel 2007 e posteriores)." },
      { q: "Excel para PDF inclui gráficos?", a: "O conversor exporta dados de células em formato de tabela. Gráficos e imagens não estão incluídos." },
      { q: "O CSV é compatível com o Google Sheets?", a: "Sim. O resultado é um CSV UTF-8 padrão compatível com Google Sheets, Excel e pandas." },
    ],
  },
  de: {
    title: "Kostenlose Excel-Tools online — Excel in PDF und CSV konvertieren",
    h1: "Kostenlose Excel-Tools online",
    description: "Kostenlose Online-Excel-Tools von FileNesto: Excel in PDF umwandeln, als CSV exportieren oder eine CSV in Excel (.xlsx) importieren — ohne Registrierung, Dateien nie gespeichert.",
    intro: "Schnelle, kostenlose Tools zum Konvertieren Ihrer Tabellenkalkulationen.",
    seoText: "Die Excel-Suite von FileNesto bietet drei wesentliche Konvertierungstools, alle kostenlos und ohne Registrierung verfügbar.",
    faqs: [
      { q: "Welche Excel-Tools bietet FileNesto an?", a: "Drei kostenlose Tools: Excel zu PDF, Excel zu CSV und CSV zu Excel. Alle kostenlos, keine Registrierung." },
      { q: "Welches Excel-Format wird unterstützt?", a: ".xlsx-Dateien (Excel 2007 und später)." },
      { q: "Enthält Excel zu PDF Diagramme?", a: "Der Konverter exportiert Zellendaten im Tabellenformat. Diagramme sind nicht enthalten." },
      { q: "Ist das CSV mit Google Sheets kompatibel?", a: "Ja. Das Ergebnis ist ein Standard-UTF-8-CSV, kompatibel mit Google Sheets, Excel und pandas." },
    ],
  },
};

const TOOLS_LABEL = { en: "tools", fr: "outils", es: "herramientas", pt: "ferramentas", de: "Tools" };
const ALL_TOOLS_LABEL = { en: "All Excel Tools", fr: "Tous les outils Excel", es: "Todas las herramientas Excel", pt: "Todas as ferramentas Excel", de: "Alle Excel-Tools" };

export default function ExcelTools() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const m = meta[lang] || meta.en;

  useHreflang("excel-tools", "category");

  useEffect(() => {
    document.title = `${m.title} — ${SITE_NAME}`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", m.description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [m, location.pathname]);

  return (
    <div className="min-h-screen bg-white page-fade-in" data-testid="page-excel-tools">
      <JsonLD data={buildBreadcrumb(m.h1, location.pathname)} />
      {m.faqs?.length > 0 && <JsonLD data={buildFAQSchema(m.faqs)} />}
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <Link
            to={buildLocalizedPath("", lang) || "/"}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-green-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {t.nav.home}
          </Link>
        </div>
        <header className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-green-700 bg-green-50 px-3 py-1 rounded-full mb-4">
            {t.categories.pdfBadge} · 3 {TOOLS_LABEL[lang] || TOOLS_LABEL.en}
          </span>
          <h1
            className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3 tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {m.h1}
          </h1>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">{m.intro}</p>
        </header>

        <h2 className="sr-only">{ALL_TOOLS_LABEL[lang] || ALL_TOOLS_LABEL.en}</h2>
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="excel-tools-grid"
        >
          {EXCEL_TOOLS_LIST.map((tool) => (
            <ToolCard
              key={tool.slug}
              slug={tool.slug}
              icon={tool.icon}
              color={tool.color}
              title={t.tools[tool.key]?.title || tool.key}
              description={t.tools[tool.key]?.description || ""}
            />
          ))}
        </div>

        {m.seoText && (
          <div className="mt-12 p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-slate-600 text-sm leading-relaxed">{m.seoText}</p>
          </div>
        )}

        {m.faqs?.length > 0 && (
          <div className="mt-8 max-w-3xl mx-auto">
            <ToolFAQ faqs={m.faqs} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
